import { PREMIERE_STANDALONE_STORAGE_ID } from '@mimir/common/util/constants';
import { TOAST, showToast } from '@mimir/web/eventBus';
import { getGlobalStore, appState } from '@mimir/web/store';
import type { SearchScrollResponseSlim, LoadedItem } from '@mimir/common/dto/searchResultHit';
import type { MetaDataSchema } from '@mimir/common/mdf/metadataSchema';
import type { StorageType } from '@mimir/common/dto/storageModel';
import {
  defaultAnalyticConfig,
  type TenantConfigDto,
  type AnalyticConfig,
  type NleAsset,
} from '@mimir/common/dto/tenantModel';
import { isSuperAdmin, isTenantAdmin } from '../util/security';
import { getRouterLink } from '@mimir/client-common/util/basicRoute';
import type { Route } from 'vue-router';
import { createItemReplacement, searchScroll } from '../util/api';
import type { ItemVersionDto } from '@mimir/common/dto/itemVersionDto';
import { injectItemIntoSearchResult } from '@mimir/web/store/search-module';
import { isAxiosError } from '@mimir/common/util/httpError';
import { logErrorAndShowToast } from '../util/uiError';
import type { User } from '../model/User';
import type { SearchScrollParamsType } from '@mimir/client-common/util/search';
import type { HttpQueryParameters } from '@mimir/common/util/apiClient';
import { folders } from '@mimir/web/globals/folders';
import type { ApplicationContext } from '@mimir/common/context/applicationContext';
import type { AnalyticsForDependencies } from '@mimir/client-common/upload/UploadItem';
import type { CompleteType } from '@mimir/common/util/general';
import { itemTypeFromFilename } from '@mimir/common/util/mediaContentType';
import { download } from '../util/misc';
import type { ItemMediaPlayerOps } from '../model/itemMediaPlayerOps';

export const getStorageByIdFromState = (storageId: string|undefined): StorageType | undefined => {
  if (storageId == null) {
    return undefined;
  }

  if (storageId === PREMIERE_STANDALONE_STORAGE_ID) {
    return {
      id: PREMIERE_STANDALONE_STORAGE_ID,
      name: PREMIERE_STANDALONE_STORAGE_ID,
      private: true,
      owner: PREMIERE_STANDALONE_STORAGE_ID,
    };
  }

  return getGlobalStore().getters.getStorageByIdFromState(storageId);
};

export const downloadWithoutNavigationWarning = (data: string | ArrayBuffer | Blob, filename?: string) => {
  const store = getGlobalStore();
  store.commit('preventBrowserNavigationWarning', true);
  void download(data, filename).finally(() => {
    store.commit('preventBrowserNavigationWarning', false);
  });
};

/**
 * Initiates a download of the given content as a CSV file.
 * @param csvContent The content to be downloaded as a CSV file.
 * @param fileName The name of the file to be downloaded.
 */
export function downloadCsv(csvContent: string, fileName: string): void {
  const blob: Blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  downloadWithoutNavigationWarning(blob, fileName);
}

/**
 * Replace old version with new version and removes old version from searchHits. Updates different part of
 * vuex state to replace old version.
 * @param replacement The new version to be used as current version.
 * @param item Old version that will be removed from searchHits.
 * @param route Route
 * @param loadNewVersion If `true` then user will be redirected to new version.
 */
export const openReplacement = (
  itemDetailsPageMediaPlayerOps: ItemMediaPlayerOps,
  quickPreviewMediaPlayerOps: ItemMediaPlayerOps,
  isProxyMode: boolean,
  applicationContext: ApplicationContext,
  replacement: LoadedItem,
  item: LoadedItem,
  route: Route,
  loadNewVersion:boolean = true,
) => {
  // When loaded item in item-details page and promoted version id is same the router doesn't reload.
  // This is why item.loaded remain false. In itemDetailsVideo we check item.loaded
  // to show secondPane which is quick-preview.
  if (route.params?.id === replacement.id) {
    replacement.loaded = true;
  }

  const currentFolder = folders.currentFolder;
  const store = getGlobalStore();
  injectItemIntoSearchResult(store.dispatch, {
    applicationContext,
    item: replacement,
    shouldUpdateExisting: false,
    currentFolder,
    isProxyMode,
  });
  store.commit('removeFromSearchById', item.id);
  replacement.routerLink = getRouterLink(
    item.itemType,
    { id: replacement.id },
    route.query,
  );
  if (loadNewVersion) {
    store.dispatch('openItem', { item: replacement, replaceHistory: true, quickPreviewMediaPlayerOps });
    store.dispatch('setItemPageItem', { item: replacement, applicationContext, isProxyMode, itemDetailsPageMediaPlayerOps });
  }
  store.commit('removeFromSelectedSearchItems', item);
  store.commit('addToSelectedSearchItems', replacement);
};


export const createNewItemVersion = async (
  isProxyMode: boolean,
  applicationContext: ApplicationContext,
  item: LoadedItem,
  createVersionRequest: ItemVersionDto,
  route: Route,
  itemDetailsPageMediaPlayerOps: ItemMediaPlayerOps,
  quickPreviewMediaPlayerOps: ItemMediaPlayerOps,
) => {
  try {
    const replacement = await createItemReplacement(item.id, createVersionRequest);
    openReplacement(itemDetailsPageMediaPlayerOps, quickPreviewMediaPlayerOps, isProxyMode, applicationContext, replacement, item, route);
  } catch (err: any) {
    const errResponse = (isAxiosError(err) && err.response?.data?.error) || {};
    const { statusCode, message, payload } = errResponse;
    if (statusCode === 409 && payload) {
      const replacement = payload as LoadedItem;
      if (replacement.id && replacement.replacementOf) {
        showToast('A new version of this item has already been created', TOAST.INFO);
        openReplacement(itemDetailsPageMediaPlayerOps, quickPreviewMediaPlayerOps, isProxyMode, applicationContext, replacement, item, route);
      }
      return;
    }
    const msg = (statusCode === 403 || statusCode === 409) && message;

    logErrorAndShowToast(`Failed to create new item version${ msg ? ': ' + msg : '.'}`, errResponse);
  }
};

export const isMdfTypeVisibleToUser = (mdf: MetaDataSchema) => {
  if (!mdf.visibleTo || isTenantAdmin() || isSuperAdmin()) return true;

  const userGroups = (getGlobalStore().getters.user as User).groups;
  for (const group of userGroups) {
    if (mdf.visibleTo.includes(group)) {
      return true;
    }
  }

  return false;
};

export const isPremiereAssetVisibleToUser = (asset: NleAsset) => {
  if (!asset.visibleTo || isTenantAdmin() || isSuperAdmin()) return true;

  const userGroups = (getGlobalStore().getters.user as User).groups;
  for (const group of userGroups) {
    if (asset.visibleTo.includes(group)) {
      return true;
    }
  }

  return false;
};

export const setTimezoneInClient = (timezone: string | undefined) => {
  appState.dateHelper.setTimezone(timezone);
};


export async function* getItemsFromLastSearchParams(): AsyncGenerator<Pick<SearchScrollResponseSlim, 'items' | 'total'>> {
  const lastSearchParams = getGlobalStore().getters.lastSearchParams as HttpQueryParameters;
  const queryParamsSearch: SearchScrollParamsType = { ...lastSearchParams, includeFolders: false, scroll: true, fetchFatVersion: false };
  let firstCallDone = false;
  let mScrollId = '';

  while (true) {
    if (firstCallDone && !mScrollId) {
      return;
    }

    const params: SearchScrollParamsType = !firstCallDone ? queryParamsSearch : {mScrollId, fetchFatVersion: false};
    const data = (await searchScroll(params)) as SearchScrollResponseSlim;
    if (!firstCallDone) {
      firstCallDone = true;
    }
    mScrollId = data.mScrollId ?? '';
    yield { items: data.items, total: data.total };
  }
}

/**
 * Retrieves the analytic configuration with fallback options.
 * The order of priority: `analyticsForDependencies` -> `tenantDefault` -> `defaultConfig`.
 */
export const getAnalyticConfigWithFallback = (
  fileName: string,
  analyticsForDependencies: AnalyticsForDependencies | undefined,
): CompleteType<AnalyticConfig> => {
  const defaultConfig: AnalyticConfig = defaultAnalyticConfig();
  const tenantDefault = (getGlobalStore().getters.tenantConfig as TenantConfigDto)?.analyticConfig;

  const itemType = itemTypeFromFilename(fileName);
  const analyticConfig = analyticsForDependencies?.[`${itemType}Config` as keyof AnalyticsForDependencies];

  const getConfigValue = <T>(key: keyof AnalyticConfig): T =>
    (analyticConfig?.[key] ?? tenantDefault?.[key] ?? defaultConfig[key]) as T;

  return {
    transcriptionEnabled: getConfigValue('transcriptionEnabled'),
    labelDetectionEnabled: getConfigValue('labelDetectionEnabled'),
    textDetectionEnabled: getConfigValue('textDetectionEnabled'),
    personDetectionEnabled: getConfigValue('personDetectionEnabled'),
    celebrityDetectionEnabled: getConfigValue('celebrityDetectionEnabled'),
    transcriptionConfig: getConfigValue('transcriptionConfig'),
    personDetectionConfig: getConfigValue('personDetectionConfig'),
    textDetectionSettings: getConfigValue('textDetectionSettings'),
    analyticPlugins: getConfigValue('analyticPlugins'),
  };
};
